import type { StepperProps } from 'fitify-ui-onboarding/src/components/GenderFunnel/Header/Stepper/Stepper.Types'
import { themeColors as figmaThemeColors } from 'fitify-ui-onboarding/src/theme/generated/theme-colors'
import type { Route } from 'fitify-ui-onboarding/src/types/routeTypes'
import {
  ColorTheme,
  FigmaColorTheme,
  type Theme,
} from 'fitify-ui-onboarding/src/types/theme'
import type { UseGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { evaluateRouteCondition } from 'fitify-ui-onboarding/src/utils/navigation'

export const FORM_ID = 'form'

export const getHeaderStepper = (
  pages: Route<string>[],
  currentPage?: Route<string>,
  data?: Record<string, unknown>
): StepperProps | undefined => {
  if (currentPage?.cluster && !currentPage.isExcludedFromProgressBarOrStepper) {
    const currentPageInAllPagesIndex = pages.findIndex(
      (page) => page.path === currentPage.path
    )

    const pagesWithCluster = pages.reduce<string[]>(
      (acc, page, allPagesIndex) => {
        if (page.cluster) {
          // Only evaluate the condition if the page is before or at the current page in the list of all pages
          const shouldEvaluateCondition =
            allPagesIndex <= currentPageInAllPagesIndex

          if (
            !page.condition ||
            (shouldEvaluateCondition && evaluateRouteCondition(page, data))
          ) {
            acc.push(page.path)
          }
        }

        return acc
      },
      []
    )

    const currentPageIndex = pagesWithCluster.indexOf(currentPage.path)

    return {
      currentPage: Math.max(0, currentPageIndex + 1),
      totalPages: pagesWithCluster.length,
    }
  }

  return undefined
}

export const resolveF01ThemeVariant = (
  growthBook: UseGrowthBook,
  onboardingVariant?: string
): Pick<Theme, 'colors' | 'type'> => {
  const colorTheme = !onboardingVariant
    ? growthBook.evalFeature('f01_color_theme')?.value
    : 'blue_light'
  switch (colorTheme) {
    case 'blue_light':
      return {
        colors: figmaThemeColors[FigmaColorTheme.BlueLight],
        type: ColorTheme.BlueLight,
      }
    case 'brown_light':
      return {
        colors: figmaThemeColors[FigmaColorTheme.BrownLight],
        type: ColorTheme.BrownLight,
      }
    case 'coral_light':
      return {
        colors: figmaThemeColors[FigmaColorTheme.CoralLight],
        type: ColorTheme.CoralLight,
      }
    case 'soft_orchid_light':
      return {
        colors: figmaThemeColors[FigmaColorTheme.SoftOrchidLight],
        type: ColorTheme.SoftOrchidLight,
      }
    case 'emerald_green_light':
    default:
      return {
        colors: figmaThemeColors[FigmaColorTheme.EmeraldGreenLight],
        type: ColorTheme.EmeraldGreenLight,
      }
  }
}

export const resolveM01ThemeVariant = (
  growthBook: UseGrowthBook,
  onboardingVariant?: string
): Pick<Theme, 'colors' | 'isDarkTheme' | 'type'> => {
  const colorTheme = !onboardingVariant
    ? growthBook.evalFeature('m01_color_theme')?.value
    : undefined
  switch (colorTheme) {
    case 'blue_dark':
      return {
        colors: figmaThemeColors[FigmaColorTheme.BlueDark],
        isDarkTheme: true,
        type: ColorTheme.BlueDark,
      }
    case 'blue_light':
    default:
      return {
        colors: figmaThemeColors[FigmaColorTheme.BlueLight],
        isDarkTheme: false,
        type: ColorTheme.BlueLight,
      }
  }
}
